import type { CreateCandidatePayload, CreateRequisitionPayload, HiringReason } from '../api';

/**
 * Validações dos formulários do módulo, como funções puras.
 *
 * Ficam fora dos componentes por dois motivos: a mesma regra é usada em
 * mais de uma tela (requisição nova e edição; candidato interno e
 * portal), e a tooling de teste do frontend roda `node:test` sobre
 * módulos `.ts` — sem DOM. Regra em função pura é regra testada.
 *
 * Nada aqui substitui o servidor: ele valida de novo e é quem recusa. O
 * ganho é o usuário não descobrir o erro depois de enviar.
 */

export type RequisicaoErrors = Partial<Record<keyof CreateRequisitionPayload, string>>;
export type CandidatoErrors = Partial<Record<keyof CreateCandidatePayload, string>>;

/** Decimal não negativo com até duas casas, como o CHECK do banco. */
const MONEY = /^\d+(\.\d{1,2})?$/;

/** Só dígitos e os caracteres da máscara de telefone. */
const PHONE_CHARS = /^[0-9()\-\s]+$/;

/** Telefone com DDD: fixo (10 dígitos) ou celular (11), com ou sem a máscara. */
export function isValidPhone(value?: string | null): boolean {
  if (typeof value !== 'string' || !PHONE_CHARS.test(value.trim())) return false;
  const digits = value.replace(/\D/g, '').length;
  return digits === 10 || digits === 11;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function isMoney(value?: string | null): boolean {
  return typeof value === 'string' && MONEY.test(value);
}

/** Somente dígitos — o CPF é gravado sem máscara. */
export function normalizeCpf(value: string): string {
  return (value ?? '').replace(/\D/g, '');
}

/**
 * Valida os dígitos verificadores do CPF; recusa sequências de dígito
 * repetido, que passam no cálculo mas não existem como CPF.
 *
 * Espelha `backend/src/modules/recruitment/candidates/cpf.ts` — a
 * duplicação é deliberada: o cliente precisa da regra sem round-trip, e
 * o servidor não pode confiar no cliente.
 */
export function isValidCpf(value: string): boolean {
  const cpf = normalizeCpf(value);
  if (cpf.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cpf)) return false;

  const digits = cpf.split('').map(Number);
  for (const [position, weightStart] of [
    [9, 10],
    [10, 11],
  ] as const) {
    let sum = 0;
    for (let index = 0; index < position; index += 1) {
      sum += digits[index] * (weightStart - index);
    }
    const remainder = (sum * 10) % 11;
    const expected = remainder === 10 ? 0 : remainder;
    if (digits[position] !== expected) return false;
  }
  return true;
}

/**
 * Requisição de vaga.
 *
 * A exigência de colaborador substituído vem de
 * `requiresReplacedEmployee` do motivo escolhido — dado de referência
 * carregado da API, não literal comparado com a string `substituicao`.
 */
export function validateRequisicaoForm(
  values: Partial<CreateRequisitionPayload>,
  hiringReasons: HiringReason[],
): RequisicaoErrors {
  const errors: RequisicaoErrors = {};

  if (!values.companyId) errors.companyId = 'Selecione a empresa.';
  if (!values.clientId) errors.clientId = 'Selecione o cliente da vaga.';
  if (!values.costCenterId) errors.costCenterId = 'Selecione o centro de custo.';
  if (!values.requestingArea?.trim()) errors.requestingArea = 'Informe a área solicitante.';
  if (!values.positionTitle?.trim()) errors.positionTitle = 'Informe o cargo.';
  if (!values.vacanciesCount || values.vacanciesCount < 1) {
    errors.vacanciesCount = 'Quantidade de vagas deve ser pelo menos 1.';
  }
  if (!values.hiringReasonId) errors.hiringReasonId = 'Selecione o motivo da contratação.';
  if (!isMoney(values.expectedSalary)) {
    errors.expectedSalary = 'Informe o salário previsto (ex.: 4500.00).';
  }
  if (!values.justification?.trim()) errors.justification = 'Informe a justificativa.';

  const reason = hiringReasons.find((item) => item.id === values.hiringReasonId);
  if (
    reason?.requiresReplacedEmployee &&
    !values.replacedEmployeeId &&
    !values.replacedEmployeeName?.trim()
  ) {
    errors.replacedEmployeeName = 'Motivo de substituição exige o colaborador substituído.';
  }

  for (const benefit of values.benefits ?? []) {
    if (benefit.estimatedValue && !isMoney(benefit.estimatedValue)) {
      errors.benefits = 'Valor de benefício inválido (ex.: 600.00).';
      break;
    }
  }

  return errors;
}

/** Cadastro interno de candidato — CPF imutável na edição. */
export function validateCandidatoForm(
  values: Partial<CreateCandidatePayload>,
  options: { editando?: boolean } = {},
): CandidatoErrors {
  const errors: CandidatoErrors = {};

  if (!values.fullName?.trim() || values.fullName.trim().length < 3) {
    errors.fullName = 'Informe o nome completo.';
  }
  if (!options.editando) {
    if (!values.cpf?.trim()) errors.cpf = 'Informe o CPF.';
    else if (!isValidCpf(values.cpf)) errors.cpf = 'CPF inválido.';
  }
  if (!values.email?.trim()) errors.email = 'Informe o e-mail.';
  else if (!EMAIL.test(values.email.trim())) errors.email = 'E-mail inválido.';
  if (!values.phone?.trim()) errors.phone = 'Informe o telefone.';
  else if (!isValidPhone(values.phone)) errors.phone = 'Telefone inválido. Informe DDD + número.';
  if (values.expectedSalary && !isMoney(values.expectedSalary)) {
    errors.expectedSalary = 'Pretensão inválida (ex.: 4500.00).';
  }

  return errors;
}

export interface InscricaoValues {
  fullName?: string;
  cpf?: string;
  email?: string;
  phone?: string;
  expectedSalary?: string;
  consent?: boolean;
  hasFile?: boolean;
}

/**
 * Inscrição pelo portal.
 *
 * Além do que o cadastro interno pede, aqui currículo e aceite são
 * obrigatórios — é a diferença que a spec `portal-candidato` estabelece.
 */
export function validateInscricaoForm(values: InscricaoValues): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!values.fullName?.trim() || values.fullName.trim().length < 3) {
    errors.fullName = 'Informe seu nome completo.';
  }
  if (!values.cpf?.trim()) errors.cpf = 'Informe seu CPF.';
  else if (!isValidCpf(values.cpf)) errors.cpf = 'CPF inválido.';
  if (!values.email?.trim()) errors.email = 'Informe seu e-mail.';
  else if (!EMAIL.test(values.email.trim())) errors.email = 'E-mail inválido.';
  if (!values.phone?.trim()) errors.phone = 'Informe seu telefone.';
  else if (!isValidPhone(values.phone)) errors.phone = 'Telefone inválido. Informe DDD + número.';
  if (values.expectedSalary && !isMoney(values.expectedSalary)) {
    errors.expectedSalary = 'Informe a pretensão no formato 4500.00.';
  }
  if (!values.hasFile) errors.file = 'Anexe seu currículo (PDF, DOC ou DOCX).';
  if (!values.consent) errors.consent = 'É necessário aceitar o tratamento dos seus dados.';

  return errors;
}

/** Decisão da cadeia: parecer obrigatório em reprovação e devolução. */
export function validateDecisao(
  decision: 'aprovar' | 'reprovar' | 'devolver',
  parecer?: string,
): string | null {
  if (decision === 'aprovar') return null;
  return parecer?.trim() ? null : 'Informe o parecer da decisão.';
}

/**
 * Movimentação do candidato: parecer obrigatório no retorno e na
 * reprovação; nota, quando informada, entre 0 e 10.
 */
export function validateMovimentacao(
  tipo: 'avancar' | 'retornar' | 'aprovar' | 'reprovar' | 'banco_talentos',
  input: { parecer?: string; score?: number },
): Record<string, string> {
  const errors: Record<string, string> = {};

  if ((tipo === 'retornar' || tipo === 'reprovar') && !input.parecer?.trim()) {
    errors.parecer = 'Informe o parecer.';
  }
  if (
    input.score !== undefined &&
    (Number.isNaN(input.score) || input.score < 0 || input.score > 10)
  ) {
    errors.score = 'A nota deve estar entre 0 e 10.';
  }

  return errors;
}

/** Agendamento: precisa ser data futura. */
export function validateAgendamento(scheduledAt: string, now = Date.now()): string | null {
  if (!scheduledAt) return 'Informe uma data e hora futuras.';
  const parsed = new Date(scheduledAt);
  if (Number.isNaN(parsed.getTime())) return 'Data e hora inválidas.';
  return parsed.getTime() > now ? null : 'Informe uma data e hora futuras.';
}

/** Publicação: só os campos públicos obrigatórios. */
export function validatePublicacao(values: {
  title?: string;
  description?: string;
  city?: string;
}): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!values.title?.trim()) errors.title = 'Informe o título.';
  if (!values.description?.trim()) errors.description = 'Informe a descrição.';
  if (!values.city?.trim()) errors.city = 'Informe a cidade.';
  return errors;
}
